/**
 * Regulatory Demands — the screen. Readiness by framework, the ninety-day
 * calendar, the limits crossed, the rules unverified, the undertakings
 * given, and the inspection date entered.
 */
import { KpiTargetsCard } from "../entryForms/KpiTargetsCard";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { defaultRateCard } from "../../domain/decision";
import { money } from "../../domain/intelligence";
import type { DocumentRow } from "../../domain/library";
import {
  answerRegulatory,
  buildRegulatoryDesk,
  materialForRegulatory,
  REGULATORY_MODEL_PROMPT,
  type RegulatoryDesk,
  type RegulatoryEntered,
  type Undertaking,
} from "../../domain/regulatory";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import * as store from "../decision/store";
import { useIntelligence } from "../intelligence/gather";
import { AskCard, Refs, Tiles, x as s } from "./shared";
import { useEntered } from "./store";
import { useScreenContext } from "../../assistant/screen";
import { t } from "../../i18n";

function useRegulatoryRecords(enabled: boolean) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["regulatory-records", siteId],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [documents, kpis, rateCard] = await Promise.all([
        quiet(source.listDocuments({ siteId }), [] as DocumentRow[]),
        quiet(source.listKpis(siteId), []),
        store.loadRateCard(source, siteId),
      ]);
      return { documents, kpis, rateCard: rateCard ?? defaultRateCard() };
    },
  });
}

export function RegulatoryPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const records = useRegulatoryRecords(!!gathered.data);
  const entered = useEntered<RegulatoryEntered>("executive.regulatory", {});
  const desk = useMemo<RegulatoryDesk | null>(() => {
    if (!gathered.data || !records.data) return null;
    return buildRegulatoryDesk({
      now: new Date(),
      siteCode: session.site.code,
      compliance: gathered.data.inputs.compliance,
      rules: gathered.data.inputs.rules,
      alerts: gathered.data.inputs.alerts,
      explanations: gathered.data.inputs.explanations,
      documents: records.data.documents,
      testing: gathered.data.inputs.testing,
      kpis: records.data.kpis,
      rateCard: records.data.rateCard,
      entered: entered.value,
    });
  }, [gathered.data, records.data, entered.value, session.site.code]);
  useScreenContext(
    desk
      ? {
          route: "/regulatory",
          title: "Regulatory Demands",
          summary: desk.boardLine,
          material: materialForRegulatory(desk),
          figures: desk.figures,
          own: (q) => answerRegulatory(q, desk),
          prompt: REGULATORY_MODEL_PROMPT,
          offers: [
            { label: "What falls due next?", ask: "what falls due next?" },
            { label: "Which limits are crossed?", ask: "which limits are crossed?" },
            { label: "Cost to close", ask: "what does it cost to close every finding?" },
          ],
        }
      : null,
  );
  return (
    <Page
      eyebrow={t("{site} · Executive", { site: session.site.code })}
      title="Regulatory Demands"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        <Around
          text={t(
            "What the regulator can ask for, when it's due, and what it costs to be ready.",
          )}
          token="{link}"
        >
          <Link to="/decision-assist">{t("Decision Room")}</Link>
        </Around>
      </p>
      {(gathered.isPending || records.isPending) && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {desk && (
        <div className={s.page}>
          <Tiles items={desk.tiles} currency={desk.currency} />
          {entered.savedTo && <p className={s.saved}>{entered.savedTo}</p>}
          <div className={s.two}>
            <Card
              title="The next ninety days"
              sub="Overdue first, then what's due next"
            >
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <tbody>
                    {desk.calendar.map((c, i) => (
                      <tr key={i} data-testid={`calendar-${c.kind}`}>
                        <td className={s.num}>
                          {c.at}
                          <div className={s.muted}>
                            {c.days < 0 ? t("{n} d ago", { n: -c.days }) : t("in {n} d", { n: c.days })}
                          </div>
                        </td>
                        <td>
                          <span
                            className={
                              c.state === "overdue" || c.state === "expired" || c.state === "missed"
                                ? s.tagRed
                                : c.state === "scheduled"
                                  ? s.tagBlue
                                  : s.tag
                            }
                          >
                            {t(c.state)}
                          </span>
                        </td>
                        <td>
                          <b>{c.title}</b>
                          <div className={s.muted}>
                            {t(c.kind)}
                            {c.framework ? ` · ${c.framework}` : ""}
                          </div>
                          <Refs list={c.sources} />
                        </td>
                      </tr>
                    ))}
                    {desk.calendar.length === 0 && (
                      <tr>
                        <td className={s.muted}>{t("Nothing falls due in the next ninety days.")}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
            <div className={s.page}>
              <Card
                title="Limits crossed"
                sub={t("{n} — each a finding the inspector can make from the platform's own readings", {
                  n: desk.crossed.length,
                })}
              >
                {desk.crossed.length === 0 && <p className={s.muted}>{t("No limit is crossed.")}</p>}
                <ul className={s.plain}>
                  {desk.crossed.map((c) => (
                    <li key={c.alert.id}>
                      <b>{c.alert.title}</b>
                      {" — "}
                      {c.reference
                        ? t("{reference}; about {cost} to close on the rate card", {
                            reference: c.reference,
                            cost: money(c.costToClose, desk.currency),
                          })
                        : t("no clause reference on the rule; about {cost} to close on the rate card", {
                            cost: money(c.costToClose, desk.currency),
                          })}
                      <Refs list={c.sources} />
                    </li>
                  ))}
                </ul>
              </Card>
              <Card
                title="Rules unverified and targets proposed"
                sub="findings an inspector can make without leaving the desk"
              >
                <ul className={s.plain}>
                  {desk.unverified.map((r) => (
                    <li key={r.key}>
                      <Around
                        text={
                          r.reference
                            ? t("Rule {rule} needs verification and has none ({reference}).", {
                                reference: r.reference,
                              })
                            : t("Rule {rule} needs verification and has none.")
                        }
                        token="{rule}"
                      >
                        <b>{r.name}</b>
                      </Around>
                    </li>
                  ))}
                  {desk.proposed.map((k) => (
                    <li key={k.key}>
                      <Around text={t("KPI target for {kpi} is proposed, not agreed.")} token="{kpi}">
                        <b>{k.label}</b>
                      </Around>
                    </li>
                  ))}
                  {desk.unverified.length === 0 && desk.proposed.length === 0 && (
                    <li className={s.muted}>
                      {t("Every rule that needs verification has it and every target is agreed or from the pack.")}
                    </li>
                  )}
                </ul>
              </Card>
            </div>
          </div>
          {/* build 6B: targets can be agreed here, not only read as proposed */}
          <KpiTargetsCard />
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            board={desk.boardLine}
            material={materialForRegulatory(desk)}
            figures={desk.figures}
            own={(q) => answerRegulatory(q, desk)}
            prompt={REGULATORY_MODEL_PROMPT}
            placeholder={t(
              "What falls due next? What does it cost to close every finding? Which rules are unverified?",
            )}
          />
          <details className={s.drawer}>
            <summary>
              {t("Inspection dates and commitments to the regulator")}
            </summary>
            <div className={s.drawerBody}>
              {!entered.loading && <RegulatoryForm key={entered.at ?? "seed"} entered={entered.value} onSave={(e) => void entered.save(e)} />}
            </div>
          </details>
          {desk.gaps.length > 0 && (
            <Card title="What this desk does not cover" sub="so the reader knows what is absent">
              <ul className={s.plain}>
                {desk.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}

function RegulatoryForm({ entered, onSave }: { entered: RegulatoryEntered; onSave: (e: RegulatoryEntered) => void }) {
  const [inspectionAt, setInspectionAt] = useState(entered.inspectionAt ?? "");
  const [inspector, setInspector] = useState(entered.inspector ?? "");
  const [u, setU] = useState<Omit<Undertaking, "id" | "status">>({ to: "GACA", what: "", due: "", owner: "" });
  const rows = entered.undertakings ?? [];
  return (
    <Card
      title="The inspection and the undertakings"
      sub="Commitments made to the regulator, with due dates"
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ ...entered, inspectionAt: inspectionAt || undefined, inspector: inspector || undefined });
        }}
      >
        <Field label="inspection date">
          <Input
            type="date"
            value={inspectionAt}
            onChange={(e) => setInspectionAt(e.target.value)}
            aria-label={t("inspection date")}
          />
        </Field>
        <Field label="inspector">
          <Input
            value={inspector}
            onChange={(e) => setInspector(e.target.value)}
            placeholder="GACA"
            aria-label={t("inspector")}
          />
        </Field>
        <Button type="submit">{t("Save the inspection")}</Button>
      </form>
      <div className={s.colTitle}>{t("Record an undertaking")}</div>
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!u.what.trim() || !u.due) return;
          onSave({ ...entered, undertakings: [...rows, { ...u, id: Date.now().toString(36), status: "open" }] });
          setU({ to: "GACA", what: "", due: "", owner: "" });
        }}
      >
        <Field label="to">
          <Input value={u.to} onChange={(e) => setU({ ...u, to: e.target.value })} aria-label={t("undertaking to")} />
        </Field>
        <Field label="what was promised">
          <Input value={u.what} onChange={(e) => setU({ ...u, what: e.target.value })} aria-label={t("undertaking")} />
        </Field>
        <Field label="by">
          <Input
            type="date"
            value={u.due}
            onChange={(e) => setU({ ...u, due: e.target.value })}
            aria-label={t("undertaking due")}
          />
        </Field>
        <Field label="owner">
          <Input
            value={u.owner}
            onChange={(e) => setU({ ...u, owner: e.target.value })}
            aria-label={t("undertaking owner")}
          />
        </Field>
        <Button type="submit" disabled={!u.what.trim() || !u.due}>
          {t("Record it")}
        </Button>
      </form>
      {rows.length > 0 && (
        <div className={s.tableWrap} style={{ marginTop: 10 }}>
          <table className={s.table}>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="undertaking-row">
                  <td>
                    <Around
                      text={t("{what} to {to}, by {due}, {owner}", { to: r.to, due: r.due, owner: r.owner })}
                      token="{what}"
                    >
                      <b>{r.what}</b>
                    </Around>
                  </td>
                  <td>
                    <Select
                      value={r.status}
                      onChange={(e) =>
                        onSave({
                          ...entered,
                          undertakings: rows.map((x) =>
                            x.id === r.id ? { ...x, status: e.target.value as Undertaking["status"] } : x,
                          ),
                        })
                      }
                      aria-label={t("status of {what}", { what: r.what })}
                    >
                      <option value="open">{t("open")}</option>
                      <option value="met">{t("met")}</option>
                      <option value="missed">{t("missed")}</option>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/** A translated sentence with one element set into it where its token stands. */
function Around({ text, token, children }: { text: string; token: string; children: ReactNode }) {
  const at = text.indexOf(token);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      {children}
      {text.slice(at + token.length)}
    </>
  );
}
